import { makeStyles, Slide, Theme } from '@material-ui/core';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import {
  BoardOverviewFragment,
  BoardPositionUpdate,
  GetBoardsSubscriptionSubscription,
  GetBoardsSubscriptionSubscriptionVariables,
  GetFeedBoardsQuery,
  useChangeBoardPositionMutation,
  useGetFeedBoardsQuery,
} from '../../../graphql/gen/types';
import { GET_BOARDS_SUBSCRIPTION } from '../../../graphql/subscriptions/GetBoards';
import { usePublishStatusFilter } from '../../../models/filters/usePublishStatusFilter';
import { useSelectedLabelIds } from '../../../models/filters/useSelectedLabelIds';
import { usePlexesMap, useSelectedPlexIds } from '../../../models/filters/useSelectedPlexIds';
import { spacings } from '../../../theme';
import { FilterPicker } from '../common/FilterPicker';
import { NewBoardButton } from '../post_form/NewBoardButton';
import { AnalyticsLocation, useLogAnalytics } from '../shared/logAnalytics';
import { BoardViewContainer } from './BoardViewContainer';
import { EmptyBaseTimeline } from './EmptyBaseTimeline';
import { FooterLoader } from './FooterLoader';
import { optimisticPositionUpdate } from './optimisticPositionUpdate';
import { updateWithSubscriptionItem } from './updateWithSubscriptionItem';
import { FetchMoreCallback, useInfiniteScroll } from './useInfiniteScroll';

export const PlexBaseTimeline = React.memo(
  (): React.ReactElement => {
    useLogAnalytics({ name: 'loaded page', details: { location: AnalyticsLocation.BASE } });
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const scrollContentRef = useRef<HTMLDivElement>(null);
    const [collapsedBoards, setCollapsedBoards] = useState<{ [boardId: string]: boolean }>({});
    const plexIds = useSelectedPlexIds();
    const filterPublishStatus = usePublishStatusFilter();
    const labelIds = useSelectedLabelIds();
    // TODO: handle errors
    const [changeBoardPosition] = useChangeBoardPositionMutation();
    const variables = useMemo(() => ({ plexIds, filterPublishStatus, labelIds }), [
      filterPublishStatus,
      labelIds,
      plexIds,
    ]);
    const {
      data: boardsData,
      loading: initialLoading,
      fetchMore: fetchMoreBoards,
      subscribeToMore: boardSubscribeToMore,
    } = useGetFeedBoardsQuery({
      variables,
    });
    const nextCursor = boardsData?.loggedIn?.feedBoards?.nextCursor;
    const onFetchMoreBoards: FetchMoreCallback = useCallback(
      (isCancelled) => {
        return fetchMoreBoards({
          variables: { ...variables, cursor: nextCursor },
          updateQuery: (prev: GetFeedBoardsQuery, { fetchMoreResult }) => {
            if (
              prev.loggedIn?.feedBoards == null ||
              !fetchMoreResult ||
              !fetchMoreResult.loggedIn?.feedBoards?.items.length ||
              isCancelled()
            )
              return prev;
            return {
              ...prev,
              loggedIn: {
                ...prev.loggedIn,
                feedBoards: {
                  ...prev.loggedIn?.feedBoards,
                  nextCursor: fetchMoreResult.loggedIn.feedBoards.nextCursor,
                  items: [...prev.loggedIn?.feedBoards?.items, ...fetchMoreResult.loggedIn.feedBoards.items],
                },
              },
            };
          },
        });
      },
      [fetchMoreBoards, nextCursor, variables]
    );

    const infiniteScrollLoading = useInfiniteScroll({
      containerRef: scrollContainerRef,
      contentRef: scrollContentRef,
      onFetchMore: onFetchMoreBoards,
      canFetchMore: !initialLoading && nextCursor != null,
    });

    const plexesMap = usePlexesMap();

    useEffect(() => {
      const unsubscribe = boardSubscribeToMore<
        GetBoardsSubscriptionSubscription,
        GetBoardsSubscriptionSubscriptionVariables
      >({
        document: GET_BOARDS_SUBSCRIPTION,
        variables,
        updateQuery: (prev, { subscriptionData }) => {
          const newItem = subscriptionData.data.board;
          if (!newItem) return prev;

          return {
            ...prev,
            loggedIn: {
              ...prev?.loggedIn,
              feedBoards: {
                ...prev?.loggedIn?.feedBoards,
                items: updateWithSubscriptionItem({
                  newItem,
                  existingItems: [...(prev?.loggedIn?.feedBoards?.items || [])],
                  newItemLocation: 'front',
                }).sort(boardCompare),
              },
            },
          };
        },
      });

      return unsubscribe;
    }, [boardSubscribeToMore, plexesMap, variables]);

    const classes = useStyles();
    const loading = initialLoading || infiniteScrollLoading;

    const onCollapseClicked = useCallback(
      (event: React.MouseEvent<HTMLElement, MouseEvent>, boardId: string, collapsed: boolean) => {
        if (event.altKey) {
          setCollapsedBoards(
            (boardsData?.loggedIn?.feedBoards?.items || []).reduce((acc: { [boardId: string]: boolean }, b) => {
              acc[b.id] = collapsed;
              return acc;
            }, {})
          );
        } else {
          setCollapsedBoards((prev) => ({ ...prev, [boardId]: collapsed }));
        }
      },
      [boardsData]
    );

    const boards = [...(boardsData?.loggedIn?.feedBoards?.items || [])]
      .filter((board) => !board.deleted)
      .sort(boardCompare);

    return (
      <Slide direction={'up'} in={true} mountOnEnter={true} unmountOnExit={true}>
        <div className={classes.root} ref={scrollContainerRef}>
          <div ref={scrollContentRef}>
            <div className={classes.header}>
              <FilterPicker />
              <NewBoardButton className={classes.createBtn} />
            </div>
            {!loading && boards.length === 0 && <EmptyBaseTimeline />}
            <DragDropContext
              onDragEnd={(result): void => {
                const oldIndex = result.source.index;
                const board = boards[result.source.index];
                const newIndex = result.destination?.index;

                if (newIndex != null && newIndex !== oldIndex) {
                  const newPosition = boards[newIndex].position.positionInPlex;

                  changeBoardPosition({
                    variables: { input: { boardId: board.id, newPosition } },
                    optimisticResponse: {
                      changeBoardPosition: optimisticUpdate({ boards, board, newPosition }),
                    },
                  });
                }
              }}
            >
              <DndProvider backend={HTML5Backend}>
                <Droppable droppableId="draggable-id">
                  {(provided): React.ReactElement => (
                    <div ref={provided.innerRef}>
                      {boards.map((b, i) => (
                        <BoardViewContainer
                          key={b.id}
                          board={b}
                          index={i}
                          totalFaqCount={b.faqCount}
                          nextCursor={b.questions.nextCursor}
                          plexData={plexesMap[b.plexId]}
                          mode={plexIds.length > 1 ? 'multiple_plexes' : 'single_plex'}
                          collapsed={!!collapsedBoards[b.id]}
                          onCollapseClicked={onCollapseClicked}
                          boardSubscribeToMore={boardSubscribeToMore}
                        />
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DndProvider>
            </DragDropContext>
            <FooterLoader isLoading={loading} />
          </div>
        </div>
      </Slide>
    );
  }
);

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflowY: 'auto',
    backgroundColor: '#fff',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: spacings.small,
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    left: 0,
    backgroundColor: '#fff',
    zIndex: 10,
    padding: spacings.normal,
    alignItems: 'center',
  },
  createBtn: {
    flexShrink: 0,
  },
}));

const boardCompare = (board1: BoardOverviewFragment, board2: BoardOverviewFragment): number => {
  const plexNameComparison = board1.plex.name.localeCompare(board2.plex.name);

  if (plexNameComparison !== 0) {
    return plexNameComparison;
  }

  // Show in DESC order of `positionInPlex`
  return -(board1.position.positionInPlex - board2.position.positionInPlex);
};

const optimisticUpdate = ({
  boards,
  board,
  newPosition,
}: {
  boards: BoardOverviewFragment[];
  board: BoardOverviewFragment;
  newPosition: number;
}): BoardPositionUpdate => {
  return {
    __typename: 'BoardPositionUpdate',
    updatedPositions: optimisticPositionUpdate<BoardOverviewFragment>({
      entity: board,
      entities: boards,
      newPosition,
      getPosition: (board: BoardOverviewFragment) => board.position.positionInPlex,
    }).map(({ id, updatedPosition }) => ({
      __typename: 'BoardPosition',
      id,
      positionInPlex: updatedPosition,
    })),
  };
};
